import type { OrgBrandingDto } from '@shared/api-dto';
import type { HuacaClient } from '../api/client';

/**
 * Org-logo upload — the three-step direct-to-storage flow, kept off the JSON client because the PUT
 * is cross-origin raw bytes:
 *
 *   1. client.orgLogoUploadUrl(...) → a signed PUT URL + storage path.
 *   2. PUT the bytes STRAIGHT to storage with the pinned Content-Type (a plain fetch — no auth, no JSON).
 *   3. client.confirmOrgLogo(...)   → the server stat()-verifies the object and records the logo.
 *
 * A logo is small and shown tiny, so before uploading we downscale + re-encode anything oversized or
 * an odd format. The server still enforces a hard 256KB / png|jpeg|webp cap — this is a courtesy so a
 * 4MB export from a design tool just works instead of being rejected.
 */

export const ALLOWED_LOGO_MIME = ['image/png', 'image/jpeg', 'image/webp'] as const;
/** The `accept` attribute for the file picker. */
export const LOGO_ACCEPT = ALLOWED_LOGO_MIME.join(',');

/** Compress toward this; the server's hard ceiling is 256KB. */
const TARGET_BYTES = 200 * 1024;
/** The logo renders tiny (a header icon); 320px covers even high-DPI display with room to spare. */
const MAX_DIMENSION = 320;

function withExt(name: string, ext: string): string {
  return `${name.replace(/\.[^./\\]+$/, '') || 'logo'}.${ext}`;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('לא ניתן לקרוא את קובץ התמונה.'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
}

/**
 * Downscale + re-encode a picked image toward the size target. Best-effort: an already-small allowed
 * file is returned untouched, and if the canvas is unavailable (jsdom) or anything throws the original
 * is returned and the server cap becomes the backstop. Output avoids `image/webp` canvas export
 * (unreliable on the Safari 14 floor): JPEG for a JPEG source, PNG otherwise — so a logo's transparency
 * is preserved.
 */
export async function compressLogo(file: File): Promise<File> {
  const allowed = (ALLOWED_LOGO_MIME as readonly string[]).includes(file.type);
  if (allowed && file.size <= TARGET_BYTES) return file;

  let img: HTMLImageElement;
  try {
    img = await loadImage(file);
  } catch {
    return file;
  }

  const longest = Math.max(img.width, img.height) || 1;
  const scale = Math.min(1, MAX_DIMENSION / longest);
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file; // e.g. jsdom / unsupported — let the server 256KB cap decide
  ctx.drawImage(img, 0, 0, w, h);

  const outType = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
  const quality = outType === 'image/jpeg' ? 0.85 : undefined;
  const blob = await canvasToBlob(canvas, outType, quality);
  if (!blob) return file;

  const ext = outType === 'image/jpeg' ? 'jpg' : 'png';
  return new File([blob], withExt(file.name, ext), { type: outType });
}

/**
 * Upload an org logo end to end and return the new branding state (the confirm response). Throws on
 * any step — an `ApiError` from the client calls, or a plain Error if the direct storage PUT fails.
 */
export async function uploadOrgLogo(client: HuacaClient, file: File): Promise<OrgBrandingDto> {
  const prepared = await compressLogo(file);
  const contentType = prepared.type || 'image/png';

  const { uploadUrl, storagePath } = await client.orgLogoUploadUrl({
    filename: prepared.name,
    contentType,
    sizeBytes: prepared.size,
  });

  // Straight to storage: a different origin, raw bytes, exactly the Content-Type the signed URL pins.
  const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: prepared });
  if (!put.ok) throw new Error('העלאת הלוגו נכשלה. נסו שוב.');

  return client.confirmOrgLogo({ storagePath, filename: prepared.name });
}
